import { Stack, SxProps, Theme, useTheme } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useRouter } from "next/router";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useTicketmaster } from "@/global/hooks/useTicketmaster";
import { useAppStore } from "@/global/store";
import {
  selectEvents,
  selectEventsSelected,
  selectRegularEventsSelected,
} from "@/global/store/data/purchaseSeats/events";
import { routes } from "@/global/routes";
import {
  authUIDs,
  purchaseSeatsTabUIDs,
  purchaseSeatsTabUIDs as tabUIDs,
  vendorUUIDs,
} from "@/global/config/constants";
import { selectUser } from "@/global/store/data/user";
import { useSelector } from "react-redux";
import { OutlinedIconButton } from "@/shared/base/components/OutlinedIconButton/OutlinedIconButton";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import {
  selectCartEventActive,
  selectCartManager,
} from "@/global/store/data/purchaseSeats/cartManager";
import { useFilterApis } from "../../GameFilters/useFilterApis";
import { useAppSystem } from "@/global/hooks/useAppSystem";
import { selectCheckoutIsLoading } from "@/global/store/data/purchaseSeats/checkout";
import { selectPackageSelected } from "@/global/store/data/purchaseSeats/packages";
import { useFraudPrevention } from "@/global/context/FraudPreventionProvider";
import { useTicketsdotcom } from "@/global/hooks/useTicketsdotcom";
import { useMinEventCount } from "../useMinEventCount";
import { CartBackButton, CartNextButton } from "./CartNavigationButtons";
import { useRouterExtended } from "@/global/hooks/useRouterExtended";
import { useCartHelpers } from "../useCartHelpers";
import React from "react";
import { useInternalAuth } from "@/global/hooks/useInternalAuth";
import { useBuckets } from "../Buckets/useBuckets";
import {
  BucketsProgressBar,
  PackBucketsProgressBar,
  RegularProgressBar,
  SPEventProgress,
} from "./GameSelectionProgress";
import { usePacks } from "../Packs/usePacks";
import { useDynamicPricing } from "../DynamicPricing/useDynamicPricing";
import { useRepsProduct } from "../useRepsProduct";
import { useRepsProductWarningModal } from "../modals/RepsProductModal";

import type { PacksType } from "@/types";
import { isAllMandatoryPerksSelected } from "../utils.perks";
import { usePerksState } from "../SelectPerks/usePerksManager";
import { findSpecialEvents } from "../utils.events";
import { useSelectActiveEvent } from "../ChooseSeats/useSelectActiveEvent";
import { useUnlockExclusiveAccessModal } from "../modals/UnlockExclusiveAccessModal";

export function BottomBarActionItems() {
  const { packageConfigs, uiConfigs, availableCreditEnabled } = useTeamConfig();
  const router = useRouter();
  const purchaseSeatTab = router.query.purchaseSeatTab as string;
  const { sm: smDown } = useCssMediaQuery("down");
  const theme = useTheme();
  const { isBucketsEnabledAndMobile } = useBuckets();
  const { isPackBucketsEnabledAndMobile } = usePacks();
  const { isDynamicPricingEnabled } = useDynamicPricing();
  const bucketsOrPacksEnabled =
    isBucketsEnabledAndMobile || isPackBucketsEnabledAndMobile;
  const showGameSelectionProgress =
    uiConfigs.bottomBar.showGameSelectionProgress || false;

  const allEvents = useAppStore(selectEvents);
  const specialEvents = findSpecialEvents(allEvents);

  return (
    <Stack
      direction={"row"}
      spacing={1.5}
      alignItems={"center"}
      width={
        (bucketsOrPacksEnabled || isDynamicPricingEnabled) &&
        purchaseSeatTab == purchaseSeatsTabUIDs.findGames
          ? "100%"
          : "unset"
      }
      sx={{
        [theme.breakpoints.down("sm")]: {
          justifyContent: "flex-end",
          width: "100%",
        },
      }}
    >
      {purchaseSeatTab == tabUIDs.chooseSeats ||
      purchaseSeatTab == tabUIDs.checkout ||
      purchaseSeatTab == tabUIDs.selectPerks ? (
        <CartBackButton />
      ) : null}

      {purchaseSeatTab == tabUIDs.findGames ? (
        <Stack
          direction={"row"}
          spacing={smDown ? (bucketsOrPacksEnabled ? 1 : 0.5) : 1}
          alignItems={"center"}
          width={smDown ? "100%" : "unset"}
          gap={2}
        >
          {packageConfigs.isPackageEnabled ? (
            <BackToPackageSelectionButton />
          ) : null}

          {showGameSelectionProgress &&
          isBucketsEnabledAndMobile &&
          !availableCreditEnabled ? (
            <BucketsProgressBar />
          ) : null}

          {showGameSelectionProgress &&
          isPackBucketsEnabledAndMobile &&
          !availableCreditEnabled ? (
            <PackBucketsProgressBar />
          ) : null}

          {showGameSelectionProgress &&
          !isPackBucketsEnabledAndMobile &&
          !isBucketsEnabledAndMobile &&
          !availableCreditEnabled &&
          !specialEvents.length &&
          smDown ? (
            <RegularProgressBar />
          ) : null}

          {showGameSelectionProgress &&
          !isPackBucketsEnabledAndMobile &&
          !isBucketsEnabledAndMobile &&
          !availableCreditEnabled &&
          specialEvents.length > 0 &&
          smDown ? (
            <SPEventProgress />
          ) : null}

          <ConfirmGameSelectionButton
            minWidth={bucketsOrPacksEnabled ? "115px" : "unset"}
          />
        </Stack>
      ) : null}

      {purchaseSeatTab == tabUIDs.chooseSeats ? (
        <Stack direction={"row"} spacing={1} alignItems={"center"}>
          <CartNextButton />
        </Stack>
      ) : null}

      {purchaseSeatTab == tabUIDs.checkout ? (
        <Stack direction={"row"} spacing={1} alignItems={"center"}>
          <ConfirmCheckoutButton />
        </Stack>
      ) : null}

      {purchaseSeatTab == tabUIDs.selectPerks ? (
        <ConfirmPerkSelectionButton />
      ) : null}
    </Stack>
  );
}

export const ConfirmPerkSelectionButton = () => {
  const { sm: smDown } = useCssMediaQuery("down");
  const [isLoading, setIsLoading] = React.useState(false);
  const perksState = usePerksState();
  const cartManager = useAppStore(selectCartManager);
  const isMandatoryPerksSelected = isAllMandatoryPerksSelected(
    perksState?.perkParents,
    cartManager
  );
  const {
    handlers: { onConfirmPerkSelection },
  } = useCartHelpers();

  return (
    <LoadingButton
      id={"confirm-perk-selection-btn"}
      loading={isLoading}
      size={"large"}
      disabled={isLoading || !isMandatoryPerksSelected || perksState?.hasLoadError}
      variant={"contained"}
      endIcon={<ArrowForwardRoundedIcon />}
      onClick={async () => {
        setIsLoading(true);
        await onConfirmPerkSelection();
        setIsLoading(false);
      }}
      sx={{ width: smDown ? "fit-content" : "180px", fontWeight: 600 }}
    >
      Checkout
    </LoadingButton>
  );
};

type ConfirmGameSelectionButtonProps = {
  display?: string;
  minWidth?: string;
  sx?: SxProps<Theme> | undefined;
  variant?: "contained" | "text" | "outlined";
};

export function ConfirmGameSelectionButton({
  sx,
  minWidth,
  variant = "contained",
}: ConfirmGameSelectionButtonProps) {
  const { sm: smDown } = useCssMediaQuery("down");
  const { minGameSelectionCount } = useMinEventCount();
  const { teamSchema, platform, uiConfigs } = useTeamConfig();
  const { handleConnectTm, isLoading: isAuthLoading } = useTicketmaster();
  const { getInventoryFilters } = useFilterApis();
  const user = useAppStore(selectUser);
  const selectedEvents = useAppStore(selectEventsSelected);
  const selectedRegularEvents = useAppStore(selectRegularEventsSelected);
  const selectedPackage = useAppStore(selectPackageSelected);
  const { handleCheckBrokerEmail, checkBrokerEmail } = useFraudPrevention();
  const { handleLoginOkta, oktaAuthLoading } = useTicketsdotcom();
  const { switchPurchaseSeatTab } = useCartHelpers();
  const { redirectLoading, shouldUseInternalLogin, handleInternalRedirect } =
    useInternalAuth();
  const {
    packsModal,
    selectedPack,
    isPacksEnabled,
    packBucketsRemainingCount,
    handleCheckShouldOpenPacksModal,
  } = usePacks();
  const {
    dynamicPricingModal,
    isDynamicPricingEnabled,
    handleShouldOpenDynamicPricingModal,
  } = useDynamicPricing();
  const { states: { repsProductWarningModal }, handlers } = useRepsProduct(); // prettier-ignore

  const unlockExclusiveAccessModal = useUnlockExclusiveAccessModal();

  const handleConfirmGameSelection = async () => {
    if (!user) {
      let query: any = {
        teamSchema,
        purchaseSeatTab: tabUIDs.chooseSeats,
        eventCodes: selectedEvents.map((itm) => itm.eventCode).join("-"),
      };
      if (selectedPackage) query.packageId = selectedPackage.id;

      let state = {
        authOrigin: "purchaseSeats",
        pathname: routes.purchaseSeats,
        query,
      };

      if (platform == vendorUUIDs.ticketmaster) {
        handleConnectTm(state);
      }

      if (platform == vendorUUIDs.tickesdotcom) {
        if (shouldUseInternalLogin) {
          const callback = window.btoa(JSON.stringify(state));
          return await handleInternalRedirect(
            routes.auth,
            callback,
            authUIDs.login
          );
        }

        await handleLoginOkta(state);
      }
    } else {
      // check if user is a broker
      const isBroker = await handleCheckBrokerEmail();
      if (isBroker) return;

      // moving to the next tab - choose seats
      await switchPurchaseSeatTab(tabUIDs.chooseSeats);
    }
  };

  const handleClick = async () => {
    const shouldOpenPacksModal = handleCheckShouldOpenPacksModal();
    if (shouldOpenPacksModal) {
      packsModal.set({ isOpen: shouldOpenPacksModal, hasModalOpened: true });
    }

    const shouldOpenDynamicPricingModal = handleShouldOpenDynamicPricingModal();
    if (isDynamicPricingEnabled && shouldOpenDynamicPricingModal)
      dynamicPricingModal.set({
        isOpen: shouldOpenDynamicPricingModal,
        hasModalOpened: true,
      });

    const shouldOpenRepsProductModal = handlers.handleCheckShouldOpenRepsProductModal(); // prettier-ignore
    if (shouldOpenRepsProductModal) {
      repsProductWarningModal.set({
        isOpen: shouldOpenRepsProductModal,
        hasModalOpened: true,
      });
    }

    const shouldOpenUnlockExclusiveAccessModal = uiConfigs?.unlockExclusiveAccessModal?.enabled && !unlockExclusiveAccessModal.hasModalOpened; // prettier-ignore
    if (shouldOpenUnlockExclusiveAccessModal) {
      unlockExclusiveAccessModal.set({
        isOpen: shouldOpenUnlockExclusiveAccessModal,
        hasModalOpened: true,
      });
    }

    if (
      !shouldOpenPacksModal &&
      !shouldOpenDynamicPricingModal &&
      !shouldOpenRepsProductModal &&
      !shouldOpenUnlockExclusiveAccessModal
    ) {
      await handleConfirmGameSelection();
    }
  };

  return (
    <LoadingButton
      id={"confirm-game-selection-btn"}
      loading={
        isAuthLoading ||
        getInventoryFilters.isLoading ||
        checkBrokerEmail.isLoading
      }
      size={"large"}
      disabled={
        selectedRegularEvents.length < minGameSelectionCount ||
        isAuthLoading ||
        getInventoryFilters.isLoading ||
        checkBrokerEmail.isLoading ||
        oktaAuthLoading ||
        redirectLoading ||
        (isPacksEnabled && selectedPack?.priritizePackAmountForEventCount
          ? selectedPack.amount > selectedRegularEvents.length
          : isPacksEnabled &&
            !packBucketsRemainingCount.every((itm) => itm.isComplete))
      }
      variant={variant}
      endIcon={<ArrowForwardRoundedIcon />}
      onClick={() => handleClick()}
      sx={{
        width: smDown ? "fit-content" : "180px",
        fontWeight: 600,
        minWidth: minWidth,
        ...sx,
      }}
    >
      Continue
    </LoadingButton>
  );
}

function ConfirmCheckoutButton() {
  const router = useRouterExtended();
  const { teamConfig } = useAppSystem();
  const { teamSchema } = teamConfig;
  const { sm: smDown } = useCssMediaQuery("down");
  const { activeEvent } = useSelectActiveEvent();
  const cartEventActive = useAppStore(selectCartEventActive(activeEvent));
  const isLoading = useSelector(selectCheckoutIsLoading);
  const [isScrolled, setIsScrolled] = React.useState(false);

  // prettier-ignore
  const handleSubmitCheckout = () => {
    const submitButton = document.getElementById("submit-purchaseSeats-checkout");
    const paymentDetailsComponent = document.getElementById("#payment-details-component");

    // checking if user has already scrolled past the payment details section
    const currentScroll = window.scrollY;
    const paymentDetails = paymentDetailsComponent?.offsetTop;

    if (!isScrolled && smDown && paymentDetails && currentScroll < paymentDetails) {
      // scrolling down to the payment details section with animation
      window.scrollTo({
        top: paymentDetails,
        behavior: "smooth",
      });
      setIsScrolled(true);
    } else {
      // if already scrolled down, just submit
      submitButton?.click();
    }
  };

  return (
    <>
      {/* <Box minWidth={"32px"}>
        <OutlinedIconButton
          size={smDown ? "small" : "medium"}
          onClick={handleBack}
        >
          {cancelCart.isLoading ? (
            <CircularProgress size={"24px"} />
          ) : (
            <ArrowBackRoundedIcon />
          )}
        </OutlinedIconButton>
      </Box> */}
      <LoadingButton
        loading={isLoading}
        onClick={() => handleSubmitCheckout()}
        size={smDown ? "medium" : "large"}
        disabled={false}
        variant={"contained"}
        endIcon={<ArrowForwardRoundedIcon />}
        sx={{ width: smDown ? "fit-content" : "180px", fontWeight: 600 }}
      >
        Checkout
      </LoadingButton>
    </>
  );
}

function BackToPackageSelectionButton() {
  const router = useRouterExtended();
  const { sm: smDown } = useCssMediaQuery("down");

  const handleBackToPackageSelection = () => {
    router.replaceShallow({
      query: { ...router.query, purchaseSeatTab: tabUIDs.packages },
    });
  };
  return (
    <OutlinedIconButton
      size={smDown ? "small" : "medium"}
      onClick={() => handleBackToPackageSelection()}
    >
      <ArrowBackRoundedIcon />
    </OutlinedIconButton>
  );
}
